import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { MenuItem, Select, Switch } from '@material-ui/core';
import GlobalAnnouncementService from '../../../services/GlobalAnnouncementService/GlobalAnnouncementService';
import { alertState } from '../../../recoil-store/recoil-state';
import { useSetRecoilState } from 'recoil';
import Spinner from '../../../components/Spinner/Spinner';
import './GlobalAnnouncement.css';
import { FIELD_LIMITS } from './globalAnnouncement.constants';
import MarkdownGuide from './MarkdownGuide';
import { toUtcDateTimeLocalValue, toUtcIsoString } from './globalAnnouncement.datetime';

const TYPE_OPTIONS = ['info', 'release', 'maintenance', 'security', 'feature'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high'];
const CONTENT_TYPES = ['plain', 'markdown'];

const emptyState = {
    title: '',
    summary: '',
    content: '',
    contentType: 'plain',
    type: 'release',
    priority: 'medium',
    ctaLabel: '',
    ctaUrl: '',
    isActive: true,
    isTest: false,
    publishStartAt: '',
    publishEndAt: ''
};

function GlobalAnnouncementForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const label = 'CONTENT.GLOBAL-ANNOUNCEMENT.';
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAlert = useSetRecoilState(alertState);

    const [state, setState] = useState(emptyState);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isEdit) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            const response = await GlobalAnnouncementService.getById(id);
            if (response?.status && response?.data) {
                const data = response.data;
                setState({
                    title: data.title || '',
                    summary: data.summary || '',
                    content: data.content || '',
                    contentType: data.contentType || 'plain',
                    type: data.type || 'info',
                    priority: data.priority || 'medium',
                    ctaLabel: data.ctaLabel || '',
                    ctaUrl: data.ctaUrl || '',
                    isActive: data.isActive !== false,
                    isTest: data.isTest === true,
                    publishStartAt: toUtcDateTimeLocalValue(data.publishStartAt),
                    publishEndAt: toUtcDateTimeLocalValue(data.publishEndAt)
                });
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(label + 'NOT-FOUND'),
                    severity: 'error'
                }));
                navigate('/management/global-announcement');
            }
            setLoading(false);
        };

        fetchData();
    }, [id, isEdit, navigate, setAlert, t]);

    const handleChange = (field, value, limit) => {
        const nextValue = limit ? value.slice(0, limit) : value;
        setState(prev => ({ ...prev, [field]: nextValue }));
    };

    const renderCharCount = (value, limit) => (
        <div className={`ga-char-count${value.length >= limit ? ' limit-reached' : ''}`}>
            {value.length}/{limit}
        </div>
    );

    const validateFieldLimits = () => {
        const checks = [
            { value: state.title, limit: FIELD_LIMITS.title, key: 'TITLE-EXCEEDS-LIMIT' },
            { value: state.summary, limit: FIELD_LIMITS.summary, key: 'SUMMARY-EXCEEDS-LIMIT' },
            { value: state.content, limit: FIELD_LIMITS.content, key: 'CONTENT-EXCEEDS-LIMIT' },
            { value: state.ctaLabel, limit: FIELD_LIMITS.ctaLabel, key: 'CTA-LABEL-EXCEEDS-LIMIT' },
            { value: state.ctaUrl, limit: FIELD_LIMITS.ctaUrl, key: 'CTA-URL-EXCEEDS-LIMIT' }
        ];

        for (const check of checks) {
            if (check.value && check.value.length > check.limit) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(label + check.key, { limit: check.limit }),
                    severity: 'error'
                }));
                return false;
            }
        }

        return true;
    };

    const validateForm = () => {
        if (!state.title.trim()) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'TITLE-REQUIRED'),
                severity: 'error'
            }));
            return false;
        }

        if (!validateFieldLimits()) {
            return false;
        }

        if ((state.ctaLabel && !state.ctaUrl) || (!state.ctaLabel && state.ctaUrl)) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'CTA-INCOMPLETE'),
                severity: 'error'
            }));
            return false;
        }

        if (state.publishStartAt && state.publishEndAt) {
            const start = toUtcIsoString(state.publishStartAt);
            const end = toUtcIsoString(state.publishEndAt);
            if (start && end && new Date(start) > new Date(end)) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(label + 'INVALID-DATE-RANGE'),
                    severity: 'error'
                }));
                return false;
            }
        }

        return true;
    };

    const buildPayload = () => ({
        title: state.title.trim(),
        summary: state.summary.trim() || null,
        content: state.content.trim() || null,
        contentType: state.contentType,
        type: state.type,
        priority: state.priority,
        isActive: state.isActive,
        isTest: state.isTest,
        publishStartAt: toUtcIsoString(state.publishStartAt),
        publishEndAt: toUtcIsoString(state.publishEndAt),
        cta: state.ctaLabel && state.ctaUrl
            ? { label: state.ctaLabel.trim(), url: state.ctaUrl.trim() }
            : null
    });

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSaving(true);
        const payload = buildPayload();
        const response = isEdit
            ? await GlobalAnnouncementService.update(id, payload)
            : await GlobalAnnouncementService.create(payload);
        setSaving(false);

        if (response?.status) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'SAVE-SUCCESS'),
                severity: 'success'
            }));
            navigate('/management/global-announcement');
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + (response?.message || 'SAVE-FAILED')),
                severity: 'error'
            }));
        }
    };

    if (loading) {
        return (
            <div className="ga-form-page">
                <div className="ga-empty"><Spinner color="#002742" /></div>
            </div>
        );
    }

    return (
        <div className="ga-form-page">
            <div className="ga-form-top">
                <div className="ga-form-header">
                    <div className="ga-header-text">
                        <h1>{t(isEdit ? label + 'EDIT-TITLE' : label + 'ADD-TITLE')}</h1>
                        <p>{t(label + 'FORM-DESCRIPTION')}</p>
                    </div>
                    <button className="ga-secondary-btn" onClick={() => navigate('/management/global-announcement')}>
                        {t(label + 'BACK')}
                    </button>
                </div>
            </div>

            <div className="ga-form-body">
                <div className="ga-form-grid">
                <section className="ga-panel">
                    <h2>{t(label + 'CONTENT-SECTION')}</h2>

                    <div className="ga-field">
                        <label>{t(label + 'TITLE')} *</label>
                        <input
                            value={state.title}
                            onChange={(e) => handleChange('title', e.target.value, FIELD_LIMITS.title)}
                            placeholder={t(label + 'TITLE-PLACEHOLDER')}
                            maxLength={FIELD_LIMITS.title}
                        />
                        {renderCharCount(state.title, FIELD_LIMITS.title)}
                    </div>

                    <div className="ga-field">
                        <label>{t(label + 'SUMMARY')}</label>
                        <input
                            value={state.summary}
                            onChange={(e) => handleChange('summary', e.target.value, FIELD_LIMITS.summary)}
                            placeholder={t(label + 'SUMMARY-PLACEHOLDER')}
                            maxLength={FIELD_LIMITS.summary}
                        />
                        {renderCharCount(state.summary, FIELD_LIMITS.summary)}
                        <div className="ga-field-hint">{t(label + 'SUMMARY-HINT')}</div>
                    </div>

                    <div className="ga-field">
                        <label>{t(label + 'CONTENT-FORMAT')}</label>
                        <div className="ga-segmented">
                            {CONTENT_TYPES.map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    className={state.contentType === type ? 'active' : ''}
                                    onClick={() => handleChange('contentType', type)}
                                >
                                    {t(`${label}FORMAT-${type.toUpperCase()}`)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {state.contentType === 'markdown' && <MarkdownGuide />}

                    <div className="ga-field">
                        <label>{t(label + 'CONTENT')}</label>
                        <textarea
                            value={state.content}
                            onChange={(e) => handleChange('content', e.target.value, FIELD_LIMITS.content)}
                            placeholder={state.contentType === 'markdown'
                                ? t(label + 'CONTENT-MARKDOWN-PLACEHOLDER')
                                : t(label + 'CONTENT-PLAIN-PLACEHOLDER')}
                            maxLength={FIELD_LIMITS.content}
                        />
                        {renderCharCount(state.content, FIELD_LIMITS.content)}
                        <div className="ga-field-hint">
                            {state.contentType === 'markdown'
                                ? t(label + 'CONTENT-MARKDOWN-HINT')
                                : t(label + 'CONTENT-OPTIONAL-HINT')}
                        </div>
                    </div>
                </section>

                <div>
                    <section className="ga-panel" style={{ marginBottom: 20 }}>
                        <h2>{t(label + 'SETTINGS-SECTION')}</h2>

                        <div className="ga-field">
                            <label>{t(label + 'TYPE')}</label>
                            <Select
                                fullWidth
                                variant="outlined"
                                value={state.type}
                                onChange={(e) => handleChange('type', e.target.value)}
                            >
                                {TYPE_OPTIONS.map(type => (
                                    <MenuItem key={type} value={type}>
                                        {t(`${label}TYPE-${type.toUpperCase()}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>

                        <div className="ga-field">
                            <label>{t(label + 'PRIORITY')}</label>
                            <Select
                                fullWidth
                                variant="outlined"
                                value={state.priority}
                                onChange={(e) => handleChange('priority', e.target.value)}
                            >
                                {PRIORITY_OPTIONS.map(priority => (
                                    <MenuItem key={priority} value={priority}>
                                        {t(`${label}PRIORITY-${priority.toUpperCase()}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>

                        <div className="ga-field">
                            <label>{t(label + 'PUBLISH-START-UTC')}</label>
                            <input
                                type="datetime-local"
                                value={state.publishStartAt}
                                onChange={(e) => handleChange('publishStartAt', e.target.value)}
                            />
                        </div>

                        <div className="ga-field">
                            <label>{t(label + 'PUBLISH-END-UTC')}</label>
                            <input
                                type="datetime-local"
                                value={state.publishEndAt}
                                onChange={(e) => handleChange('publishEndAt', e.target.value)}
                            />
                            <div className="ga-field-hint ga-publish-timezone-hint">{t(label + 'PUBLISH-DATE-TIMEZONE-HINT')}</div>
                        </div>

                        <div className="ga-switch-row">
                            <span>{t(label + 'ACTIVE')}</span>
                            <Switch
                                checked={state.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                color="primary"
                            />
                        </div>

                        <div className="ga-switch-row">
                            <div>
                                <span>{t(label + 'TEST-PUBLICATION')}</span>
                                <div className="ga-field-hint">{t(label + 'TEST-PUBLICATION-HINT')}</div>
                            </div>
                            <Switch
                                checked={state.isTest}
                                onChange={(e) => handleChange('isTest', e.target.checked)}
                                color="primary"
                            />
                        </div>
                    </section>

                    <section className="ga-panel">
                        <h2>{t(label + 'CTA-SECTION')}</h2>
                        <p className="ga-field-hint" style={{ marginBottom: 16 }}>{t(label + 'CTA-DESCRIPTION')}</p>

                        <div className="ga-field">
                            <label>{t(label + 'CTA-LABEL')}</label>
                            <input
                                value={state.ctaLabel}
                                onChange={(e) => handleChange('ctaLabel', e.target.value, FIELD_LIMITS.ctaLabel)}
                                placeholder={t(label + 'CTA-LABEL-PLACEHOLDER')}
                                maxLength={FIELD_LIMITS.ctaLabel}
                            />
                            {renderCharCount(state.ctaLabel, FIELD_LIMITS.ctaLabel)}
                        </div>

                        <div className="ga-field">
                            <label>{t(label + 'CTA-URL')}</label>
                            <input
                                value={state.ctaUrl}
                                onChange={(e) => handleChange('ctaUrl', e.target.value, FIELD_LIMITS.ctaUrl)}
                                placeholder="https://..."
                                maxLength={FIELD_LIMITS.ctaUrl}
                            />
                            {renderCharCount(state.ctaUrl, FIELD_LIMITS.ctaUrl)}
                            <div className="ga-field-hint">{t(label + 'CTA-URL-HINT')}</div>
                        </div>
                    </section>
                </div>
                </div>
            </div>

            <div className="ga-form-actions">
                <button className="ga-secondary-btn" onClick={() => navigate('/management/global-announcement')}>
                    {t(label + 'CANCEL')}
                </button>
                <button className="ga-primary-btn" onClick={handleSubmit} disabled={saving}>
                    {saving ? t(label + 'SAVING') : t(isEdit ? label + 'UPDATE' : label + 'SAVE')}
                </button>
            </div>
        </div>
    );
}

export default GlobalAnnouncementForm;
